/**
 * Bird's capability gate.
 *
 * Deliberately lives apart from `useWorkspaceRole.ts`: this hook needs
 * `AuthContext`, which transitively initialises Firebase at module scope. Only
 * the handful of Bird surfaces pay that import cost, so the widely-imported
 * role hooks stay a Firebase-free leaf.
 */

import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { normaliseWorkspaceRole } from './useWorkspaceRole';

/**
 * Every resolved workspace role — including the read-only GUEST/VIEWER pair —
 * can see the Chat entry point. The write boundary lives one layer down, at
 * the MCP tool gate (`FORBIDDEN_VIEWER_WRITE`) and the agent-service runtime
 * checks, not here: this hook only decides who SEES the control. An
 * unrecognised role still fails closed. The one deliberately relaxed case is
 * the first response after workspace creation: older/cached create payloads
 * may not contain `userRole` yet, but `createdById` still proves that the
 * signed-in user owns that workspace. This prevents a new owner seeing a Chat
 * button that cannot open.
 */
export function useCanUseBird(): boolean {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const rawRole = workspace?.userRole;

  if (typeof rawRole === 'string') {
    // Any role `normaliseWorkspaceRole` recognises may chat; only an
    // unrecognised string fails closed.
    return normaliseWorkspaceRole(rawRole) !== null;
  }

  return Boolean(
    workspace?.createdById && user?.internalId && workspace.createdById === user.internalId,
  );
}
